/**
 * Firebase Analytics implementation with GDPR compliance
 * Only initializes after user consent is granted
 */

export default class Analytics {
  constructor() {
    this.isInitialized = false;
    this.consentKey = 'analytics-consent';
  this.eventsBound = false; // prevent double-binding listeners
  this.measurementId = 'G-QR8KQXDBF0';
  this.debugView = false; // when true, send debug_mode to GA4
    
    // Expose init method globally for debug purposes
    window.initAnalytics = () => this.initializeAnalytics();
    
    // Expose debug methods globally for production testing
    window.enableAnalyticsDebug = () => {
      this.enableDebugView();
      sessionStorage.setItem('ga4-debug-session', 'true');
      console.info('🔍 Analytics Debug Mode Enabled for Session');
      console.info('📊 Open GA4 DebugView: Configure → DebugView');
      console.info('🎯 Events will now appear in real-time DebugView');
      console.info('💾 Debug mode will persist across page navigation');
    };
    
    window.disableAnalyticsDebug = () => {
      this.debugView = false;
      sessionStorage.removeItem('ga4-debug-session');
      console.info('🔇 Analytics Debug Mode Disabled');
      console.info('💾 Debug mode removed from session');
      if (typeof window.gtag === 'function' && this.isInitialized) {
        window.gtag('config', this.measurementId, { debug_mode: false });
      }
    };
  }

  /**
   * Initialize the Analytics module and set up event listeners
   */
  init() {
    // Check for existing consent with fallback
    const analyticsConsent = this.getStoredConsent();
    
    // Check if debug mode is enabled for this session
    try {
      if (sessionStorage.getItem('ga4-debug-session') === 'true') {
        this.debugView = true;
        console.info('🔍 Analytics Debug Mode restored from session');
      }
    } catch (error) {
      // Ignore sessionStorage errors
    }
    
    if (analyticsConsent === 'accepted') {
      console.log("[Analytics] Consent already accepted, initializing...");
      this.initializeAnalytics();
    }
    
    // Listen for consent events
    document.addEventListener('analytics:consent', (event) => {
      console.log("[Analytics] Consent event received:", event.detail);
      if (event.detail && event.detail.accepted === true) {
        this.initializeAnalytics();
      }
    });
    
    // If user changes page, we should recheck consent and initialize if needed
    // This handles page navigation without full page reload (if applicable)
    window.addEventListener('popstate', () => {
      const currentConsent = this.getStoredConsent();
      if (currentConsent === 'accepted' && !this.isInitialized) {
        this.initializeAnalytics();
      }
    });
  }

  /**
   * Safely get stored consent with fallback
   */
  getStoredConsent() {
    try {
      return localStorage.getItem(this.consentKey);
    } catch (error) {
      console.warn('[Analytics] localStorage not available, consent cannot be persisted');
      return null;
    }
  }

  /**
   * Safely store consent with fallback
   */
  setStoredConsent(value) {
    try {
      localStorage.setItem(this.consentKey, value);
      return true;
    } catch (error) {
      console.warn('[Analytics] localStorage not available, consent cannot be persisted');
      return false;
    }
  }

  /**
   * Load and initialize Firebase Analytics
   */
  initializeAnalytics() {
    // Prevent double initialization
    if (this.isInitialized) {
      return;
    }
    
    // Local development: single debug path
    // - Always bind event tracking with a safe stubbed trackEvent
    // - Do not load external SDKs
    if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
      console.log('[Analytics] Local development mode - using stubbed tracking');
      
      // Mark initialized so consumers know analytics is ready for testing
      this.isInitialized = true;
      
      // If a debug panel or other script already provided trackEvent, don't override it
      if (typeof window.trackEvent !== 'function') {
        window.trackEvent = (eventName, params = {}) => {
          // Keep logging minimal here; dedicated debug panel may handle visualization
          // Use debug level to reduce noise
          console.debug('[Analytics DEV] trackEvent', eventName, params);
          return true;
        };
      }
      
      // Bind event tracking once
      this.setupEventTracking();
      return;
    }
    
    // GA4 via gtag.js
    // Set up dataLayer/gtag shim before loading the library
    window.dataLayer = window.dataLayer || [];
    function gtag(){ window.dataLayer.push(arguments); }
    window.gtag = window.gtag || gtag;

    // Consent defaults (defensive). We're loading after consent, but keep explicit update.
    window.gtag('consent', 'default', {
      'analytics_storage': 'denied',
      'ad_storage': 'denied',
      'ad_user_data': 'denied',
      'ad_personalization': 'denied'
    });

    // Load the GA4 script
    const gaScript = document.createElement('script');
    gaScript.async = true;
    gaScript.src = `https://www.googletagmanager.com/gtag/js?id=${this.measurementId}`;
    gaScript.onload = () => {
      try {
        // Initialize gtag
        window.gtag('js', new Date());

        // Update consent (analytics granted, ads denied by default)
        window.gtag('consent', 'update', {
          'analytics_storage': 'granted',
          'ad_storage': 'denied',
          'ad_user_data': 'denied',
          'ad_personalization': 'denied'
        });

        // Configure GA4 (auto page_view)
        window.gtag('config', this.measurementId, {
          anonymize_ip: true,
          page_path: window.location.pathname,
          page_title: document.title,
          page_location: window.location.href
        });

        // Mark initialized
        this.isInitialized = true;

        // Expose global trackEvent if not already provided
        if (typeof window.trackEvent !== 'function') {
          window.trackEvent = (eventName, params = {}) => {
            if (!this.isInitialized) return false;
            try {
              const payload = params || {};
              if (this.debugView) payload.debug_mode = true;
              window.gtag('event', eventName, payload);
              return true;
            } catch (error) {
              console.error('[Analytics] Error tracking event:', error);
              return false;
            }
          };
        }

        // Bind event tracking once
        this.setupEventTracking();
        console.log('[Analytics] GA4 initialized successfully');
      } catch (error) {
        console.error('[Analytics] Error initializing GA4:', error);
      }
    };
    
    gaScript.onerror = () => {
      console.error('[Analytics] Failed to load GA4 script');
      // Provide fallback trackEvent that doesn't break the site
      if (typeof window.trackEvent !== 'function') {
        window.trackEvent = (eventName, params = {}) => {
          console.debug('[Analytics] GA4 unavailable, event not tracked:', eventName, params);
          return false;
        };
      }
    };
    
    document.body.appendChild(gaScript);
  }

  // Enable GA4 DebugView for this session
  enableDebugView() {
    this.debugView = true;
    if (typeof window.gtag === 'function' && this.isInitialized) {
      window.gtag('config', this.measurementId, { debug_mode: true });
    }
    console.info('[Analytics] GA4 DebugView enabled - events will appear in GA4 DebugView');
  }
  
  /**
   * Set up tracking for common user interactions
   */
  setupEventTracking() {
    // Avoid binding twice across re-inits or navigations
    if (this.eventsBound) {
      return;
    }
    this.eventsBound = true;
    
    import('./trackingEvents.js').then(module => {
      // Setup link tracking
      if (typeof module.setupLinkTracking === 'function') {
        module.setupLinkTracking();
        console.log("[Analytics] Link tracking initialized");
      }
      
      // Setup form tracking
      if (typeof module.setupFormTracking === 'function') {
        module.setupFormTracking();
        console.log("[Analytics] Form tracking initialized");
      }
      
      // Setup search tracking
      if (typeof module.setupSearchTracking === 'function') {
        module.setupSearchTracking();
        console.log("[Analytics] Search tracking initialized");
      }
    }).catch(error => {
      console.error("[Analytics] Error loading tracking events:", error);
    });
  }
}
